import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import type { SupportImage } from "../../lib/api-client/support";
import { ImageLightbox } from "../ImageLightbox";

/** Authenticated image reads belong to this ticket/profile mount; access loss and unmount revoke every local URL. */
export function SupportMessageImages({ ticketId, images, admin, onAccessLost }: {
  ticketId: string; images: SupportImage[]; admin: boolean; onAccessLost: (cause: unknown) => boolean;
}) {
  const [loaded, setLoaded] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string[]>([]);
  const [retry, setRetry] = useState(0), [expanded, setExpanded] = useState<string | null>(null);
  const reject = useRef(onAccessLost); reject.current = onAccessLost;
  useEffect(() => {
    const abort = new AbortController(), urls: string[] = [];
    setLoaded({}); setFailed([]); setExpanded(null);
    for (const image of images) {
      void api.supportImage(ticketId, image.id, admin, abort.signal).then(blob => {
        if (abort.signal.aborted) return;
        const url = URL.createObjectURL(blob); urls.push(url);
        setLoaded(current => ({ ...current, [image.id]: url }));
      }).catch((cause: unknown) => { if (!abort.signal.aborted && !reject.current(cause)) setFailed(current => [...current, image.id]); });
    }
    return () => { abort.abort(); urls.forEach(url => URL.revokeObjectURL(url)); };
  }, [ticketId, images, admin, retry]);
  const visible = images.filter(image => loaded[image.id]);
  return <div className="flex flex-wrap gap-2">
    {images.map(image => loaded[image.id] ? <button key={image.id} type="button" aria-label={`View screenshot ${image.position + 1}`} onClick={() => setExpanded(image.id)} className="h-28 w-32 max-w-full rounded-lg bg-navy-50 focus-visible:ring-2 focus-visible:ring-navy">
      <img src={loaded[image.id]} alt={`Screenshot ${image.position + 1}`} className="h-full w-full object-contain" />
    </button> : <div key={image.id} className="rounded-lg bg-navy-50 p-3 text-sm">
      {failed.includes(image.id) ? <button type="button" className="min-h-11 underline" onClick={() => setRetry(value => value + 1)}>Retry image</button> : <span role="status">Loading image…</span>}
    </div>)}
    {expanded && <ImageLightbox images={visible.map(image => ({ src: loaded[image.id], alt: `Screenshot ${image.position + 1}` }))} initialIndex={visible.findIndex(image => image.id === expanded)} onClose={() => setExpanded(null)} />}
  </div>;
}
